import React, { useEffect } from 'react';
import feather from 'feather-icons';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * サイドバーは「何をする画面か」でまとめる。
 *
 * 13項目が見出しも無く一列に並んでいて、Store / Store Review / Store Import /
 * Store Media / Video Linking / Video Scan / Video Store Link が区別できない、
 * という指摘から。順番は作った順で、意味は無かった。
 *
 * 分け方は担当する仕事に合わせている。店舗マスタを育てる人、動画を集めて店に
 * 結びつける人、投稿を見る人、数字を見る人、取引先を見る人。
 *
 * 表記は日本語。使うのは社内の運営で、画面の見出しはもともと日本語
 * （「店舗管理」「店舗レビュー」「動画の店舗紐付け」）なのに、サイドバーだけが
 * 英語で別の名前を名乗っていた。サイドバーの名前は画面の見出しに合わせる。
 */
const adminSections = [
  {
    items: [
      { to: '/dashboard', icon: 'bar-chart-2', label: 'ダッシュボード' },
    ],
  },
  {
    label: '店舗',
    items: [
      { to: '/stores', icon: 'map-pin', label: '店舗管理' },
      { to: '/stores/review', icon: 'check-square', label: '店舗レビュー' },
      { to: '/stores/add-requests', icon: 'plus-square', label: '店舗の追加申請' },
      { to: '/chains', icon: 'layers', label: 'チェーン' },
      { to: '/import/stores', icon: 'upload', label: '店舗の一括取り込み' },
    ],
  },
  {
    // 借りてきた動画（YouTube）と自分たちで撮った素材は別の仕事 ──
    // 前者は「どの店のものか決める」、後者は「解析にかける」── だが、
    // 見出しを分けるほど項目が無い。順番でそれを表している。
    label: '動画・素材',
    items: [
      { to: '/videos', icon: 'youtube', label: 'YouTube動画' },
      { to: '/videos/link-queue', icon: 'link', label: '動画の紐付け確認' },
      { to: '/store-media', icon: 'hard-drive', label: '撮影素材' },
      { to: '/video-upload', icon: 'film', label: '動画スキャン' },
      { to: '/poc', icon: 'crosshair', label: '動画スキャンPoC' },
      // 自動公開の元栓。いま置いてあるのは1つだけだが、「当分は off」という
      // 判断に日付と名前を付けられる場所が今日まで無かった。
      { to: '/settings', icon: 'sliders', label: '設定（自動公開）' },
    ],
  },
  {
    label: 'ユーザー投稿',
    items: [
      { to: '/users', icon: 'users', label: 'ユーザー管理' },
      { to: '/contributors/candidates', icon: 'award', label: '認定コントリビューター' },
      { to: '/posts/moderation', icon: 'shield', label: '投稿の審査' },
      { to: '/images/queue', icon: 'image', label: '投稿画像の承認' },
      { to: '/scans', icon: 'camera', label: 'スキャンデータ' },
    ],
  },
  {
    label: '分析',
    items: [
      { to: '/analytics/kpi', icon: 'activity', label: 'KPI' },
      { to: '/analytics/prizes', icon: 'award', label: '景品の分析' },
      { to: '/analytics/stores', icon: 'trending-up', label: '店舗の分析' },
    ],
  },
  {
    label: '取引先',
    items: [
      { to: '/b2b/accounts', icon: 'briefcase', label: '店舗アカウント' },
      { to: '/partners', icon: 'user-check', label: 'パートナー管理' },
    ],
  },
];

const partnerSections = [
  {
    items: [
      { to: '/partner/dashboard', icon: 'bar-chart-2', label: 'ダッシュボード' },
      { to: '/partner/scans', icon: 'camera', label: '自分のスキャン' },
      { to: '/partner/stores', icon: 'map-pin', label: '店舗チェックリスト' },
      { to: '/partner/earnings', icon: 'dollar-sign', label: '報酬' },
    ],
  },
];

function Sidebar({ collapsed, onToggle }) {
  const { isPartner } = useAuth();
  const sections = isPartner ? partnerSections : adminSections;

  useEffect(() => {
    feather.replace();
  });

  return (
    <nav
      className="sidebar-nav text-white position-fixed top-0 start-0 vh-100 d-flex flex-column"
      style={{
        width: collapsed ? '60px' : '250px',
        transition: 'width 0.2s ease',
        zIndex: 1030,
        overflow: 'hidden',
      }}
    >
      <div
        className="d-flex align-items-center border-bottom border-secondary"
        style={{ padding: collapsed ? '0.75rem 0' : '0.75rem 1rem', justifyContent: collapsed ? 'center' : 'space-between' }}
      >
        {!collapsed && (
          <div>
            <h5 className="mb-0 fw-bold" style={{ letterSpacing: '0.04em' }}>NekotoWan</h5>
            <small style={{ color: '#8a94ad', fontSize: '0.75rem' }}>
              {isPartner ? 'Partner' : 'Admin'}
            </small>
          </div>
        )}
        <button
          onClick={onToggle}
          className="btn btn-link text-white p-0 d-flex align-items-center justify-content-center"
          style={{ width: 28, height: 28, flexShrink: 0 }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <i data-feather={collapsed ? 'chevrons-right' : 'chevrons-left'} style={{ width: 16, height: 16 }}></i>
        </button>
      </div>

      <ul className="nav nav-pills flex-column mt-2 flex-grow-1" style={{ padding: collapsed ? '0 6px' : '0 8px' }}>
        {sections.map((section, si) => (
          <React.Fragment key={si}>
            {section.label && !collapsed && (
              <li className="nav-item mt-3 mb-1 px-2">
                <small className="text-uppercase fw-semibold" style={{ color: '#525b75', fontSize: '0.6875rem', letterSpacing: '0.06em' }}>
                  {section.label}
                </small>
              </li>
            )}
            {section.label && collapsed && (
              <li className="nav-item mt-2 mb-1">
                <hr style={{ borderColor: '#525b75', margin: '0.25rem 0' }} />
              </li>
            )}
            {section.items.map((item) => (
              <li className="nav-item" key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    'nav-link text-white d-flex align-items-center rounded' +
                    (isActive ? ' active-link' : ' hover-bg') +
                    (collapsed ? ' justify-content-center' : ' gap-2')
                  }
                  style={{
                    fontSize: '0.8125rem',
                    padding: collapsed ? '0.5rem' : '0.5rem 0.75rem',
                  }}
                  title={collapsed ? item.label : undefined}
                >
                  <i data-feather={item.icon} style={{ width: 16, height: 16, flexShrink: 0 }}></i>
                  {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>

      <div className="border-top border-secondary" style={{ padding: collapsed ? '0.75rem 0' : '0.75rem 1rem', textAlign: 'center' }}>
        <small style={{ color: '#525b75', fontSize: '0.6875rem' }}>{collapsed ? 'v0.3' : 'v0.3.0'}</small>
      </div>
    </nav>
  );
}

export default Sidebar;
